import * as THREE from "three";
import { Lod } from "./lod";
import { ShadowProxies } from "../shadowProxies";
import { CpuRelease } from "../cpuRelease";

/**
 * The battle's towers and fortress at two lower details, made once per source geometry and shared by every copy.
 *
 * They were drawn at full detail — 30–47 k triangles a tower, 100–119 k the fortress — twice a frame, in the main pass
 * and again in the shadow pass: 39–42 % of a battle frame (RC1 perf audit). Now:
 * - each casting mesh casts through a reduced twin (`Shadow`) drawn only into the shadow map (shadowProxies.ts); a
 *   shadow is a soft dark shape at the shadow map's resolution, where a fifth of the triangles draw the same outline;
 * - zoomed out past EnemyViews.LodZoom (where the grunts switch too) the main pass draws `Far`, the reduction the
 *   hub's fortress and towers already stand at (HUB_BUDGET.ModelLod), seen there from about the same distance.
 *
 * Every attribute is carried per kept vertex (skin indices and weights included), so the rigid rigs animate the
 * reduced meshes unchanged: a proxy shares its mesh's skeleton and bind matrices, so the bone atlas offset
 * (boneAtlas.ts) reaches it too. Until the simplifier has compiled (Lod.ready) a model stays as it was built.
 */
export class TowerLod {
  /** The shadow proxy: a fifth of the triangles, allowed 1 % of the mesh's extent of error. */
  static readonly Shadow = { ratio: 0.2, error: 0.01 } as const;
  /** The far detail (HUB_BUDGET.ModelLod). */
  static readonly Far = { ratio: 0.4, error: 0.004 } as const;
  /** Meshes under this keep their one detail (glow cards, pennants, small parts): not worth a copy. */
  static readonly MinTriangles = 2_000;

  /** Reduced copies by source geometry and target; null when the source does not reduce usefully. Library-lived. */
  private static readonly made = new WeakMap<THREE.BufferGeometry, Map<string, THREE.BufferGeometry | null>>();
  /** A mesh's two main-pass geometries; the far one is built the first time it is shown. */
  private static readonly details = new WeakMap<THREE.Mesh, { full: THREE.BufferGeometry; far: THREE.BufferGeometry | null }>();

  /** Fits a freshly built model with its shadow proxies and far detail (drawn at once when `far`). */
  static attach(root: THREE.Object3D, far: boolean): void {
    if (!Lod.ready) return;
    const meshes: THREE.Mesh[] = [];
    root.traverse((o) => { if (TowerLod.eligible(o)) meshes.push(o as THREE.Mesh); });
    for (const m of meshes) {
      const full = m.geometry;
      // The far detail's reduction (the simplifier: the slow part) is worked out now; its geometry is only copied out
      // the first time the main pass shows it (farOf). Built here, a twin the player never zoomed out to see was never
      // uploaded, so it kept its arrays for the session (M31: ~3 MB a level in the headless run).
      if (TowerLod.reducesFar(full)) {
        const d = { full, far: null as THREE.BufferGeometry | null };
        TowerLod.details.set(m, d);
        if ((m as THREE.SkinnedMesh).isSkinnedMesh) CpuRelease.boundsFromFull(m as THREE.SkinnedMesh, full);
        if (far) m.geometry = TowerLod.farOf(d);
      }
      if (!m.castShadow) continue;
      const shadowGeo = TowerLod.reduced(full, TowerLod.Shadow);
      if (!shadowGeo) continue;
      m.castShadow = false;
      m.add(TowerLod.proxyFor(m, shadowGeo));
    }
  }

  /** Switches every fitted mesh under `root` to its far or its full detail. */
  static setFar(root: THREE.Object3D, far: boolean): void {
    root.traverse((o) => {
      const d = TowerLod.details.get(o as THREE.Mesh);
      if (d) (o as THREE.Mesh).geometry = far ? TowerLod.farOf(d) : d.full;
    });
  }

  /** Whether `full` has a far detail: its copy when made already (its reduction is then dropped), else the reduction. */
  private static reducesFar(full: THREE.BufferGeometry): boolean {
    const made = TowerLod.made.get(full)?.get(`${TowerLod.Far.ratio}|${TowerLod.Far.error}`);
    if (made !== undefined) return made !== null;
    return Lod.reduction(full, full, TowerLod.Far.ratio, TowerLod.Far.error) !== null;
  }

  /** A mesh's far geometry, shared by every copy of its source: copied out of the cached reduction on first use. */
  private static farOf(d: { full: THREE.BufferGeometry; far: THREE.BufferGeometry | null }): THREE.BufferGeometry {
    d.far ??= TowerLod.reduced(d.full, TowerLod.Far) ?? d.full;
    return d.far;
  }

  private static eligible(o: THREE.Object3D): boolean {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (m as THREE.InstancedMesh).isInstancedMesh || Array.isArray(m.material) || o.userData.shadowProxy === true) return false;
    const g = m.geometry;
    if (!g?.index || g.index.count / 3 < TowerLod.MinTriangles || g.groups.length > 1 || Object.keys(g.morphAttributes).length > 0) return false;
    return !Object.values(g.attributes).some((a) => (a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute);
  }

  /**
   * The reduction of `geo` for `lod`, made on first use. The copies are draw-only — nothing reads their vertices
   * again; a skinned mesh measures its bounds on its full detail (CpuRelease.boundsFromFull) — so their arrays go
   * once the main renderer has uploaded them (cpuRelease.ts), and they are disposed with their source: the library
   * unloading a model (AssetLibrary.release) disposes its geometry, and the copies' GPU buffers (and the renderer's
   * vertex-array state, which holds their attributes) used to live on after it (M31).
   */
  private static reduced(geo: THREE.BufferGeometry, lod: { readonly ratio: number; readonly error: number }): THREE.BufferGeometry | null {
    let byLod = TowerLod.made.get(geo);
    if (!byLod) {
      const copies = new Map<string, THREE.BufferGeometry | null>();
      byLod = copies;
      TowerLod.made.set(geo, copies);
      const onDispose = (): void => {
        geo.removeEventListener("dispose", onDispose);
        for (const c of copies.values()) c?.dispose();
        TowerLod.made.delete(geo);
      };
      geo.addEventListener("dispose", onDispose);
    }
    const id = `${lod.ratio}|${lod.error}`;
    if (!byLod.has(id)) {
      const r = Lod.reduction(geo, geo, lod.ratio, lod.error);
      const copy = r ? Lod.apply(geo, r) : null;
      if (copy) { copy.name = `${geo.name}~${id}`; CpuRelease.geometry(copy); }
      byLod.set(id, copy);
      Lod.forget(geo, lod.ratio, lod.error);            // the copy is cached above for the source's life
    }
    return byLod.get(id)!;
  }


  /** A caster-only twin of `m` at the mesh's own transform (a child at identity), sharing its rig and bounds. */
  private static proxyFor(m: THREE.Mesh, geo: THREE.BufferGeometry): THREE.Mesh {
    const skin = m as THREE.SkinnedMesh;
    let proxy: THREE.Mesh;
    if (skin.isSkinnedMesh) {
      const s = new THREE.SkinnedMesh(geo, m.material);
      s.skeleton = skin.skeleton;
      s.bindMode = skin.bindMode;
      s.bindMatrix = skin.bindMatrix;                   // the same objects: the bone atlas writes its offset into them
      s.bindMatrixInverse = skin.bindMatrixInverse;
      s.boundingBox = skin.boundingBox;
      s.boundingSphere = skin.boundingSphere;
      // its own geometry's arrays are released after upload (reduced): it takes its mesh's bounds, measured on the
      // full detail, whenever three asks for them
      s.computeBoundingBox = function (this: THREE.SkinnedMesh): void { if (!skin.boundingBox) skin.computeBoundingBox(); this.boundingBox = skin.boundingBox; };
      s.computeBoundingSphere = function (this: THREE.SkinnedMesh): void { if (!skin.boundingSphere) skin.computeBoundingSphere(); this.boundingSphere = skin.boundingSphere; };
      proxy = s;
    } else proxy = new THREE.Mesh(geo, m.material);
    proxy.name = `${m.name}~shadow`;
    proxy.customDepthMaterial = m.customDepthMaterial;
    proxy.customDistanceMaterial = m.customDistanceMaterial;
    proxy.castShadow = true;
    proxy.receiveShadow = false;
    proxy.frustumCulled = m.frustumCulled;
    ShadowProxies.add(proxy);
    return proxy;
  }
}
